/**
 * Category Service
 *
 * CRUD operations for categories.
 * All operations are local-first (IndexedDB).
 */

import { db, Category } from './db';
import { generateId } from '../utils/id';
import { syncManager } from './syncManager';
import { dataEvents } from './dataEvents';

// ============================================================
// Types
// ============================================================

export type CategoryInput = Omit<Category, 'id'>;

// ============================================================
// Debug Logging
// ============================================================

const DEBUG =
  import.meta.env.VITE_DEBUG === 'true' ||
  (typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).has('debug'));

function log(...args: unknown[]): void {
  if (DEBUG) {
    console.log('[CAT]', ...args);
  }
}

// ============================================================
// CRUD Operations
// ============================================================

export const categoryService = {
  /**
   * Create a new category.
   */
  async create(data: CategoryInput): Promise<string> {
    const id = generateId();

    await db.categories.add({
      ...data,
      id,
    });

    log('Created category:', id, data.name);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('categories');

    return id;
  },

  /**
   * Get a category by ID.
   */
  async getById(id: string): Promise<Category | undefined> {
    return db.categories.get(id);
  },

  /**
   * Get a category by name.
   */
  async getByName(name: string): Promise<Category | undefined> {
    return db.categories.where('name').equalsIgnoreCase(name).first();
  },

  /**
   * Get all categories.
   */
  async getAll(): Promise<Category[]> {
    return db.categories.toArray();
  },

  /**
   * Get active categories only.
   */
  async getActive(): Promise<Category[]> {
    return db.categories.filter((c) => c.isActive === true).toArray();
  },

  /**
   * Get categories by type (income/expense).
   */
  async getByType(type: 'income' | 'expense'): Promise<Category[]> {
    return db.categories.where('type').equals(type).toArray();
  },

  /**
   * Get expense categories.
   */
  async getExpenseCategories(): Promise<Category[]> {
    return this.getByType('expense');
  },

  /**
   * Get income categories.
   */
  async getIncomeCategories(): Promise<Category[]> {
    return this.getByType('income');
  },

  /**
   * Get subcategories of a parent.
   */
  async getSubcategories(parentId: string): Promise<Category[]> {
    return db.categories.where('parentId').equals(parentId).toArray();
  },

  /**
   * Update a category.
   * BUG-015 FIX: Validates type changes - cannot change type if transactions exist with old type.
   * Returns { success: false, error } if type change is blocked.
   */
  async update(id: string, data: Partial<Omit<Category, 'id'>>): Promise<{ success: boolean; error?: string }> {
    // BUG-015 FIX: If changing type, check for transactions with the old type
    if (data.type) {
      const category = await this.getById(id);
      if (category && category.type !== data.type) {
        // Check for active transactions using this category with the OLD type
        const transactions = await db.transactions
          .where('categoryId')
          .equals(id)
          .filter(t => !t.deletedAt && t.type === category.type)
          .toArray();

        if (transactions.length > 0) {
          log('Cannot change category type - has', transactions.length, 'transactions with old type');
          return {
            success: false,
            error: `Cannot change type: ${transactions.length} ${category.type} transactions use this category`
          };
        }
      }
    }

    await db.categories.update(id, {
      ...data,
      updatedAt: new Date(),
    });
    log('Updated category:', id);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('categories');
    return { success: true };
  },

  /**
   * Delete a category.
   * BUG-005 FIX: Now checks if category is in use by transactions before deleting.
   * Returns { success: false, inUseCount, scheduleCount } if category has references.
   */
  async delete(id: string): Promise<{ success: boolean; inUseCount?: number; scheduleCount?: number }> {
    // Check if category is used by any active transactions
    const transactions = await db.transactions
      .where('categoryId')
      .equals(id)
      .filter(t => !t.deletedAt)  // Only count active transactions
      .toArray();

    if (transactions.length > 0) {
      log('Cannot delete category - in use by', transactions.length, 'transactions');
      return { success: false, inUseCount: transactions.length };
    }

    // Check if category is used by any active scheduled transactions
    // Note: categoryId is not indexed on scheduledTransactions, so we use filter() instead of where()
    const schedules = await db.scheduledTransactions
      .filter(s => s.categoryId === id && !s.deletedAt && !s.isIgnoredPermanently)
      .toArray();

    if (schedules.length > 0) {
      log('Cannot delete category - in use by', schedules.length, 'scheduled transactions');
      return { success: false, scheduleCount: schedules.length };
    }

    await db.categories.delete(id);
    log('Deleted category:', id);

    // Trigger debounced sync
    syncManager.triggerPush();

    // Notify UI to refresh
    dataEvents.emit('categories');
    return { success: true };
  },

  /**
   * Deactivate a category (soft-delete).
   */
  async deactivate(id: string): Promise<{ success: boolean; error?: string }> {
    const result = await this.update(id, { isActive: false });
    if (result.success) {
      log('Deactivated category:', id);
    }
    return result;
  },

  /**
   * Activate a category.
   */
  async activate(id: string): Promise<{ success: boolean; error?: string }> {
    const result = await this.update(id, { isActive: true });
    if (result.success) {
      log('Activated category:', id);
    }
    return result;
  },

  /**
   * Get category count.
   */
  async count(): Promise<number> {
    return db.categories.count();
  },

  /**
   * Check if category exists.
   */
  async exists(name: string): Promise<boolean> {
    const category = await this.getByName(name);
    return category !== undefined;
  },
};
